import {useState} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {MRT_ColumnDef, MRT_PaginationState} from 'material-react-table';

import {Pool} from '@entities/pool';
import {stakeColumns, useGetPoolStakes} from '@entities/stake';
import {CurrencyValue, MrTable} from '@shared/components';
import {entityTableCommonProps} from '@shared/constants';

const outcomeColumns: MRT_ColumnDef<Pool['outcomes'][number]>[] = [
  {accessorKey: 'eventSlug', header: 'Event'},
  {accessorKey: 'eventStatus', header: 'Status'},
  {
    accessorKey: 'real',
    header: 'Real',
    Cell: ({row}) => <CurrencyValue value={row.original.real} />,
  },
  {
    accessorKey: 'promo',
    header: 'Promo',
    Cell: ({row}) => <CurrencyValue value={row.original.promo} />,
  },
  {accessorKey: 'participants', header: 'Participants'},
  {
    accessorKey: 'voided',
    header: 'Voided',
    Cell: ({row}) => (row.original.voided ? 'Yes' : 'No'),
  },
];

export const PoolDetailsDialog = ({
  pool,
  kind,
  onClose,
}: {
  pool: Pool;
  kind: 'user' | 'topic';
  onClose: () => void;
}) => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const {
    data: stakes,
    isPending,
    error,
  } = useGetPoolStakes(
    {
      groupId: pool.groupId,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    },
    {enabled: kind === 'topic'},
  );
  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth={false}
      sx={{'& .MuiDialog-paper': {maxWidth: 1500}}}
      aria-labelledby="pool-details-title"
    >
      <DialogTitle
        id="pool-details-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        Pool {pool.groupId}
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>Winner event ID: {pool.winnerEventId ?? '—'}</Typography>
          <Typography fontWeight="bold">Outcomes</Typography>
          <MrTable
            columns={outcomeColumns}
            data={pool.outcomes}
            {...entityTableCommonProps}
            enableRowActions={false}
          />
          {kind === 'user' ? (
            <>
              <Typography fontWeight="bold">User stakes</Typography>
              <MrTable
                columns={stakeColumns}
                data={pool.stakes ?? []}
                {...entityTableCommonProps}
                enableRowActions={false}
              />
            </>
          ) : (
            <>
              <Typography fontWeight="bold">All pool stakes</Typography>
              <MrTable
                columns={stakeColumns}
                data={stakes?.items ?? []}
                error={error}
                enablePagination
                manualPagination
                enableSorting={false}
                onPaginationChange={setPagination}
                rowCount={stakes?.total ?? 0}
                state={{isLoading: isPending, pagination}}
                {...entityTableCommonProps}
                enableRowActions={false}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
